import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import type { Timestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import type { CodeRequest } from "@event-planner/shared";

/**
 * Admin read shape for a `codeRequests` row (ADR-030). Status union comes
 * from the shared schema so 'dismissed' (ADR-170) lives in one place;
 * `requestedAt` narrows to the client SDK's Timestamp.
 */
export interface CodeRequestDoc {
  id: string;
  email: string;
  requestedAt: Timestamp;
  status: CodeRequest["status"];
}

const codeRequestsCollection = () => collection(db, "codeRequests");

export async function fetchPendingCodeRequests(): Promise<CodeRequestDoc[]> {
  const q = query(codeRequestsCollection(), where("status", "==", "pending"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as CodeRequestDoc);
}

/**
 * Dismiss a pending request: it leaves the Users-page banner but the row is
 * kept as an audit record. Non-sticky — the same email can request again and
 * a fresh 'pending' row would reappear (ADR-170). Never call this on a
 * 'fulfilled' row: those are the durable signal `checkEmailStatus` routes on
 * (ADR-154); security rules only permit transitions out of 'pending'.
 */
export async function dismissCodeRequest(
  id: string,
  dismissedByUid: string,
): Promise<void> {
  await updateDoc(doc(db, "codeRequests", id), {
    status: "dismissed",
    dismissedAt: serverTimestamp(),
    dismissedByUid,
  });
}

/**
 * Mark every pending request matching `email` as fulfilled, so the banner
 * clears after a successful create flow. Per-row failures are swallowed:
 * the create flow must not fail because a banner row couldn't be updated —
 * the request just stays pending until the organizer dismisses it.
 */
export async function markCodeRequestsFulfilledByEmail(
  email: string,
  fulfilledByUid: string,
): Promise<void> {
  const normalized = email.toLowerCase();
  const pending = await fetchPendingCodeRequests();
  const matches = pending.filter(
    (r) => r.email.toLowerCase() === normalized,
  );
  for (const m of matches) {
    try {
      await updateDoc(doc(db, "codeRequests", m.id), {
        status: "fulfilled",
        fulfilledAt: serverTimestamp(),
        fulfilledByUid,
      });
    } catch {
      // non-fatal
    }
  }
}
